import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useVoice } from '../context/VoiceContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, Loader2, Plus, Trash2, MessageSquare, Mic, MicOff, Image, Newspaper, Search, Volume2, VolumeX, X, ChevronDown, Briefcase, Heart, GraduationCap, Sparkles } from 'lucide-react';
import { GlassCard } from '../components/layout/GlassCard';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Personality Modes
const PERSONALITY_MODES = [
  { id: 'assistant', name: 'Professional', icon: Briefcase, description: 'Helpful & efficient', color: 'from-blue-500 to-cyan-500' },
  { id: 'friend', name: 'Friendly', icon: Heart, description: 'Warm & supportive', color: 'from-pink-500 to-rose-500' },
  { id: 'mentor', name: 'Mentor', icon: GraduationCap, description: 'Wise & guiding', color: 'from-purple-500 to-indigo-500' },
  { id: 'fun', name: 'Fun Mode', icon: Sparkles, description: 'Playful & creative', color: 'from-amber-500 to-orange-500' },
];

export default function ChatPage() {
  const { token } = useAuth();
  const { voiceEnabled, speak, stopSpeaking, isSpeaking } = useVoice();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [showSessions, setShowSessions] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [quickAction, setQuickAction] = useState(null); // 'image', 'news'
  const [personalityMode, setPersonalityMode] = useState('assistant');
  const [showPersonalityMenu, setShowPersonalityMenu] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const recognitionRef = useRef(null);
  const personalityMenuRef = useRef(null);

  // Close personality menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (personalityMenuRef.current && !personalityMenuRef.current.contains(e.target)) {
        setShowPersonalityMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load user preferences on mount
  useEffect(() => {
    const loadPreferences = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/memory/preferences`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        });
        if (response.data?.personality_mode) {
          setPersonalityMode(response.data.personality_mode);
        }
      } catch (error) {
        console.log('Using default personality mode');
      }
    };
    loadPreferences();
  }, [token]);

  // Save personality mode when changed
  const handlePersonalityChange = async (modeId) => {
    setPersonalityMode(modeId);
    setShowPersonalityMenu(false);
    
    try {
      await axios.put(
        `${API_URL}/api/memory/preferences`,
        { personality_mode: modeId },
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        }
      );
      toast.success(`Switched to ${PERSONALITY_MODES.find(m => m.id === modeId)?.name} mode`);
    } catch (error) {
      console.error('Failed to save preference');
    }
  };

  // Initialize Web Speech Recognition
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'en-US';
    }
    
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
    };
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadSessions = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/chat/sessions`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setSessions(response.data);
    } catch (error) {
      console.error('Failed to load sessions:', error);
    }
  }, [token]);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  const loadSession = async (sid) => {
    try {
      const response = await axios.get(`${API_URL}/api/chat/session/${sid}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setSessionId(sid);
      setMessages(response.data.messages || []);
      setShowSessions(false);
    } catch (error) {
      toast.error('Failed to load session');
    }
  };

  const startNewChat = () => {
    setSessionId(null);
    setMessages([]);
    setShowSessions(false);
    inputRef.current?.focus();
  };

  const deleteSession = async (sid, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API_URL}/api/chat/session/${sid}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setSessions(sessions.filter(s => s.session_id !== sid));
      if (sessionId === sid) {
        startNewChat();
      }
      toast.success('Chat deleted');
    } catch (error) {
      toast.error('Failed to delete chat');
    }
  };

  const sendMessage = useCallback(async (e, voiceMessage = null) => {
    e?.preventDefault();
    const messageText = voiceMessage || input.trim();
    if (!messageText || loading) return;

    const userMessage = messageText;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage, timestamp: new Date().toISOString() }]);
    setLoading(true);
    
    // Add placeholder for streaming response
    setMessages(prev => [...prev, { role: 'assistant', content: '', timestamp: new Date().toISOString(), isStreaming: true }]);

    try {
      const response = await fetch(
        `${API_URL}/api/chat/stream`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          credentials: 'include',
          body: JSON.stringify({
            message: userMessage,
            session_id: sessionId,
            module: 'chat'
          })
        }
      );

      if (!response.ok) throw new Error('Stream failed');
      
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullResponse = '';
      let newSessionId = sessionId;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              
              if (data.chunk) {
                fullResponse += data.chunk;
                // Update the streaming message in real-time
                setMessages(prev => prev.map((msg, idx) => 
                  idx === prev.length - 1 && msg.isStreaming 
                    ? { ...msg, content: fullResponse }
                    : msg
                ));
              }
              
              if (data.session_id) {
                newSessionId = data.session_id;
              }
              
              if (data.done) {
                // Finalize the message
                setMessages(prev => prev.map((msg, idx) => 
                  idx === prev.length - 1 
                    ? { ...msg, isStreaming: false, content: fullResponse }
                    : msg
                ));
                setSessionId(newSessionId);
                loadSessions();
              }
              
              if (data.error) {
                throw new Error(data.error);
              }
            } catch (parseError) {
              // Ignore parse errors for incomplete chunks
            }
          }
        }
      }
      
      // Speak response if voice enabled
      if (voiceEnabled && voiceMessage && fullResponse) {
        try {
          await speak(fullResponse);
        } catch (e) {
          // TTS not available - silent fail
        }
      }
    } catch (error) {
      toast.error('Failed to send message');
      // Remove the streaming placeholder on error
      setMessages(prev => prev.filter((msg, idx) => idx !== prev.length - 1 || !msg.isStreaming));
    } finally {
      setLoading(false);
    }
  }, [input, loading, token, sessionId, loadSessions, voiceEnabled, speak]);

  // Start voice input
  const startListening = useCallback(() => {
    if (!recognitionRef.current) {
      toast.error('Speech recognition not supported');
      return;
    }

    recognitionRef.current.onstart = () => setIsListening(true);
    
    recognitionRef.current.onresult = (event) => {
      let finalTranscript = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) {
          finalTranscript += event.results[i][0].transcript;
        } else {
          setInput(event.results[i][0].transcript);
        }
      }
      if (finalTranscript) {
        sendMessage(null, finalTranscript);
      }
    };
    
    recognitionRef.current.onerror = (event) => {
      console.error('Speech error:', event.error);
      setIsListening(false);
      if (event.error === 'not-allowed') {
        toast.error('Microphone access denied');
      }
    };
    
    recognitionRef.current.onend = () => setIsListening(false);
    
    recognitionRef.current.start();
  }, [sendMessage]);

  const stopListening = useCallback(() => {
    if (recognitionRef.current) recognitionRef.current.stop();
    setIsListening(false);
  }, []);

  return (
    <div className="flex h-[calc(100vh-4rem)] gap-4" data-testid="chat-page">
      {/* Sessions Sidebar - Desktop */}
      <div className="hidden lg:block w-72">
        <GlassCard className="h-full flex flex-col">
          <div className="p-4 border-b border-white/5">
            <button
              onClick={startNewChat}
              className="btn-primary w-full flex items-center justify-center gap-2"
              data-testid="new-chat-btn"
            >
              <Plus className="w-4 h-4" />
              New Chat
            </button>
          </div>
          <div className="flex-1 overflow-y-auto custom-scrollbar p-2 space-y-1">
            {sessions.map((session) => (
              <div
                key={session.session_id}
                onClick={() => loadSession(session.session_id)}
                className={`group flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-colors ${
                  sessionId === session.session_id ? 'bg-primary/10 text-primary' : 'hover:bg-white/5'
                }`}
              >
                <MessageSquare className="w-4 h-4 flex-shrink-0" />
                <span className="flex-1 truncate text-sm">
                  {session.messages?.[0]?.content?.slice(0, 30) || 'New Chat'}...
                </span>
                <button
                  onClick={(e) => deleteSession(session.session_id, e)}
                  className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-500/20 rounded transition-all"
                >
                  <Trash2 className="w-4 h-4 text-red-400" />
                </button>
              </div>
            ))}
          </div>
        </GlassCard>
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col">
        <GlassCard className="flex-1 flex flex-col overflow-hidden">
          {/* Chat Header */}
          <div className="p-4 border-b border-white/5 flex items-center justify-between">
            <div>
              <h2 className="font-heading text-xl font-semibold">AIRA Chat</h2>
              <p className="text-sm text-gray-500">Your intelligent AI assistant</p>
            </div>
            
            {/* Personality Mode Selector */}
            <div className="flex items-center gap-3">
              <div className="relative" ref={personalityMenuRef}>
                <button
                  onClick={() => setShowPersonalityMenu(!showPersonalityMenu)}
                  className="flex items-center gap-2 px-3 py-2 bg-white/5 hover:bg-white/10 rounded-xl border border-white/10 transition-all"
                  data-testid="personality-mode-btn"
                >
                  {(() => {
                    const mode = PERSONALITY_MODES.find(m => m.id === personalityMode);
                    const Icon = mode?.icon || Briefcase;
                    return (
                      <>
                        <div className={`w-6 h-6 rounded-lg bg-gradient-to-br ${mode?.color || 'from-blue-500 to-cyan-500'} flex items-center justify-center`}>
                          <Icon className="w-3.5 h-3.5 text-white" />
                        </div>
                        <span className="text-sm font-medium hidden sm:inline">{mode?.name || 'Professional'}</span>
                        <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showPersonalityMenu ? 'rotate-180' : ''}`} />
                      </>
                    );
                  })()}
                </button>
                
                <AnimatePresence>
                  {showPersonalityMenu && (
                    <motion.div
                      initial={{ opacity: 0, y: -10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -10, scale: 0.95 }}
                      className="absolute right-0 top-full mt-2 w-56 bg-[#0a0a0f]/95 backdrop-blur-xl border border-white/10 rounded-xl shadow-2xl z-50 overflow-hidden"
                    >
                      <div className="p-2 space-y-1">
                        {PERSONALITY_MODES.map((mode) => (
                          <button
                            key={mode.id}
                            onClick={() => handlePersonalityChange(mode.id)}
                            className={`w-full flex items-center gap-3 p-3 rounded-lg transition-all ${
                              personalityMode === mode.id 
                                ? 'bg-primary/20 border border-primary/30' 
                                : 'hover:bg-white/5'
                            }`}
                            data-testid={`personality-${mode.id}`}
                          >
                            <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${mode.color} flex items-center justify-center`}>
                              <mode.icon className="w-4 h-4 text-white" />
                            </div>
                            <div className="text-left">
                              <p className="text-sm font-medium text-white">{mode.name}</p>
                              <p className="text-xs text-gray-500">{mode.description}</p>
                            </div>
                            {personalityMode === mode.id && (
                              <div className="ml-auto w-2 h-2 rounded-full bg-primary" />
                            )}
                          </button>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
              
              <button
                onClick={() => setShowSessions(!showSessions)}
                className="lg:hidden p-2 hover:bg-white/10 rounded-xl transition-colors"
              >
                <MessageSquare className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto custom-scrollbar p-4 space-y-4">
            {messages.length === 0 ? (
              <div className="h-full flex flex-col">
                {/* Original Start Conversation */}
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center max-w-md">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-r from-primary/20 to-secondary/20 flex items-center justify-center mx-auto mb-4">
                      <MessageSquare className="w-8 h-8 text-primary" />
                    </div>
                    <h3 className="font-heading text-xl font-semibold mb-2">Start a Conversation</h3>
                    <p className="text-gray-400">
                      Ask AIRA ECHO anything - from research and writing to coding and analysis.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <AnimatePresence>
                {messages.map((msg, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={msg.role === 'user' ? 'chat-message-user' : 'chat-message-ai'}>
                      <p className="whitespace-pre-wrap">
                        {msg.content}
                        {msg.isStreaming && (
                          <span className="inline-block w-2 h-5 ml-1 bg-primary animate-pulse rounded-sm" />
                        )}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            )}
            {loading && !messages.some(m => m.isStreaming) && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex justify-start"
              >
                <div className="chat-message-ai flex items-center gap-2">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                  <span className="text-gray-400">AIRA is thinking...</span>
                </div>
              </motion.div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Actions */}
          <div className="px-4 pt-3 flex items-center gap-2">
            <button
              onClick={() => setQuickAction(quickAction === 'image' ? null : 'image')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs transition-all ${
                quickAction === 'image' 
                  ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30' 
                  : 'bg-white/5 text-gray-400 hover:bg-white/10'
              }`}
            >
              <Image className="w-3.5 h-3.5" />
              Create Image
            </button>
            <button
              onClick={() => setQuickAction(quickAction === 'news' ? null : 'news')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs transition-all ${
                quickAction === 'news' 
                  ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                  : 'bg-white/5 text-gray-400 hover:bg-white/10'
              }`}
            >
              <Newspaper className="w-3.5 h-3.5" />
              Latest News
            </button>
            <button
              onClick={() => setInput('Search the web for ')}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs bg-white/5 text-gray-400 hover:bg-white/10 transition-all"
            >
              <Search className="w-3.5 h-3.5" />
              Web Search
            </button>
          </div>

          {/* Input */}
          <div className="p-4 border-t border-white/5">
            <form onSubmit={sendMessage} className="flex gap-3">
              <div className="flex-1 relative">
                <input
                  ref={inputRef}
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder={
                    quickAction === 'image' ? "Describe the image you want to create..." :
                    quickAction === 'news' ? "What news topic interests you?" :
                    "Ask AIRA ECHO anything..."
                  }
                  className="input-field w-full pr-24"
                  disabled={loading}
                  data-testid="chat-input"
                />
                
                {/* Voice input button */}
                <button
                  type="button"
                  onClick={isListening ? stopListening : startListening}
                  disabled={loading}
                  className={`absolute right-14 top-1/2 -translate-y-1/2 p-2 rounded-lg transition-all ${
                    isListening 
                      ? 'bg-red-500/20 text-red-400 animate-pulse' 
                      : 'hover:bg-white/10 text-gray-400'
                  }`}
                  data-testid="voice-input-btn"
                >
                  {isListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                </button>
                
                {/* Voice output toggle */}
                <button
                  type="button"
                  onClick={() => voiceEnabled ? stopSpeaking() : null}
                  className={`absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-lg transition-all ${
                    isSpeaking ? 'text-accent' : 'text-gray-500'
                  } hover:bg-white/10`}
                  title={voiceEnabled ? "Voice responses enabled" : "Voice responses disabled"}
                >
                  {isSpeaking ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
                </button>
              </div>
              
              <button
                type="submit"
                disabled={loading || !input.trim()}
                className="btn-primary px-6 flex items-center gap-2"
                data-testid="send-message-btn"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
              </button>
            </form>
            
            {/* Listening indicator */}
            {isListening && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center justify-center gap-2 mt-3 text-accent text-sm"
              >
                <div className="flex gap-1">
                  {[...Array(4)].map((_, i) => (
                    <motion.div
                      key={i}
                      className="w-1 bg-accent rounded-full"
                      animate={{ height: [8, 16, 8] }}
                      transition={{ duration: 0.4, repeat: Infinity, delay: i * 0.1 }}
                    />
                  ))}
                </div>
                <span>Listening... Speak now</span>
              </motion.div>
            )}
          </div>
        </GlassCard>
      </div>

      {/* Mobile Sessions Panel */}
      <AnimatePresence>
        {showSessions && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="lg:hidden fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
              onClick={() => setShowSessions(false)}
            />
            <motion.div
              initial={{ x: 300 }}
              animate={{ x: 0 }}
              exit={{ x: 300 }}
              className="lg:hidden fixed right-0 top-0 h-full w-72 bg-[#030014]/95 backdrop-blur-2xl border-l border-white/5 z-50 p-4"
            >
              <button
                onClick={startNewChat}
                className="btn-primary w-full flex items-center justify-center gap-2 mb-4"
              >
                <Plus className="w-4 h-4" />
                New Chat
              </button>
              <div className="space-y-1">
                {sessions.map((session) => (
                  <div
                    key={session.session_id}
                    onClick={() => loadSession(session.session_id)}
                    className={`group flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-colors ${
                      sessionId === session.session_id ? 'bg-primary/10 text-primary' : 'hover:bg-white/5'
                    }`}
                  >
                    <MessageSquare className="w-4 h-4 flex-shrink-0" />
                    <span className="flex-1 truncate text-sm">
                      {session.messages?.[0]?.content?.slice(0, 30) || 'New Chat'}...
                    </span>
                  </div>
                ))}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
